import React, { useRef } from 'react';

import { Icon } from 'components/Icon';
import { useOnClickOutside } from 'hooks/useOnClickOutside';
import { sumBy } from 'utils/functional/array/sumBy';

import {
  CartPillCount,
  CartPillCountAndIconContainer,
  CartPillDetails,
  CartPillLink,
  CartPillListItemCount,
  CartPillListItemEmptyMessage,
  CartPillListItemLi,
  CartPillListItemLink,
  CartPillListItemOrgName,
  CartPillListUl,
  CartPillSummary,
  CartPillSummaryChevronContainer,
} from './CartPill.styled';
import { CartPillOrgData } from './CartPillOrgData';

const cartPillQaId = 'cart-pill';
const cartPillCountQaId = 'cart-pill-count';

type Props = {
  selectedOrgId: string | undefined;
  getOrgCartDataHref: (orgCartData: CartPillOrgData) => string;
  orgsCartData: CartPillOrgData[];
  emptyHrefs: {
    postAJob: string;
    postAnInternship: string;
    postAVolop: string;
  };
};

export function CartPill({
  selectedOrgId,
  getOrgCartDataHref,
  orgsCartData,
  emptyHrefs,
}: Props) {
  const containerRef = useRef<HTMLDetailsElement>(null);
  useOnClickOutside({
    containerRef,
    onClickOutside: () => {
      containerRef.current?.removeAttribute('open');
    },
  });

  const totalItemsCount = sumBy(orgsCartData, 'itemsCount');

  const filteredOrgsCartData = orgsCartData.filter(
    (orgCartData) => orgCartData.itemsCount > 0,
  );

  const countAndIcon = (
    <CartPillCountAndIconContainer>
      {totalItemsCount > 0 && (
        <CartPillCount data-qa-id={cartPillCountQaId}>
          {totalItemsCount}
        </CartPillCount>
      )}

      <Icon name="cart" size={18} />
    </CartPillCountAndIconContainer>
  );

  if (orgsCartData.length === 1) {
    return (
      <CartPillLink
        to={getOrgCartDataHref(orgsCartData[0])}
        data-qa-id={cartPillQaId}
      >
        {countAndIcon}
      </CartPillLink>
    );
  }

  const isEmpty = filteredOrgsCartData.length === 0;

  return (
    <CartPillDetails
      ref={containerRef}
      role="list"
      data-qa-id={cartPillQaId}
      data-empty={isEmpty ? true : undefined}
    >
      <CartPillSummary>
        {countAndIcon}

        <CartPillSummaryChevronContainer>
          <Icon name="chevron-down" size={9} />
        </CartPillSummaryChevronContainer>
      </CartPillSummary>

      <CartPillListUl data-content={isEmpty ? 'placeholder' : 'orgs'}>
        {isEmpty && (
          <>
            <CartPillListItemLi>
              <CartPillListItemEmptyMessage>
                All your carts are empty
              </CartPillListItemEmptyMessage>
            </CartPillListItemLi>

            <CartPillListItemLi>
              <CartPillListItemLink
                to={emptyHrefs.postAJob}
                data-qa-id="cart-pill-list-post-a-job"
              >
                {getText('Post a Job')}
              </CartPillListItemLink>
            </CartPillListItemLi>

            <CartPillListItemLi>
              <CartPillListItemLink
                to={emptyHrefs.postAnInternship}
                data-qa-id="cart-pill-list-post-an-internship"
              >
                {getText('Post an Internship')}
              </CartPillListItemLink>
            </CartPillListItemLi>

            <CartPillListItemLi>
              <CartPillListItemLink
                to={emptyHrefs.postAVolop}
                data-qa-id="cart-pill-list-post-a-volop"
              >
                {getText('Post a Volunteer Opportunity')}
              </CartPillListItemLink>
            </CartPillListItemLi>
          </>
        )}

        {filteredOrgsCartData.map((orgCartData) => (
          <CartPillListItemLi
            key={orgCartData.id}
            data-selected={orgCartData.id === selectedOrgId}
          >
            <CartPillListItemLink to={getOrgCartDataHref(orgCartData)}>
              <CartPillListItemCount data-qa-id="cart-pill-list-item-count">
                {orgCartData.itemsCount}
              </CartPillListItemCount>

              <CartPillListItemOrgName data-qa-id="cart-pill-list-item-org-name">
                {orgCartData.name}
              </CartPillListItemOrgName>
            </CartPillListItemLink>
          </CartPillListItemLi>
        ))}
      </CartPillListUl>
    </CartPillDetails>
  );
}
